// CRM form-tracking helper.
// Sends form submission data to the CRM backend so leads are saved as contacts.
// Fire-and-forget: never blocks the form UX.

type StandardTrackingFieldKey = string;
type RegisteredCustomFieldId = string;
type TrackingCustomField = { value?: unknown; label: string };
type TrackingFileField = { file?: File; label: string };
type TrackingImageDataField = { dataUrl?: string; label: string };

const postTrackingEvent = (
  trackingPayload: Record<string, unknown> & {
    formData: Record<StandardTrackingFieldKey, unknown>;
    formLabels: Record<StandardTrackingFieldKey, string>;
  },
  options: {
    customFields?: Record<RegisteredCustomFieldId, TrackingCustomField>;
    fileFields?: Record<RegisteredCustomFieldId, TrackingFileField>;
    imageDataFields?: Record<RegisteredCustomFieldId, TrackingImageDataField>;
  } = {},
) => {
  const { customFields = {}, fileFields = {}, imageDataFields = {} } = options;
  const eventPayload = {
    ...trackingPayload,
    formData: { ...trackingPayload.formData },
    formLabels: { ...trackingPayload.formLabels },
  };
  const body = new FormData();

  for (const [key, field] of Object.entries(customFields)) {
    if (field.value === undefined) continue;
    eventPayload.formData[key] = field.value;
    eventPayload.formLabels[key] = field.label;
  }

  for (const [key, field] of Object.entries(imageDataFields)) {
    const dataUrl = field.dataUrl;
    if (!dataUrl) continue;
    if (!dataUrl.startsWith("data:image/")) {
      throw new Error("Image data field must be a data:image/* base64 string");
    }
    eventPayload.formData[key] = dataUrl;
    eventPayload.formLabels[key] = field.label;
  }

  for (const [key, field] of Object.entries(fileFields)) {
    const file = field.file;
    if (!file) continue;
    if (file.size > 50 * 1024 * 1024) {
      throw new Error("File must be 50 MB or smaller");
    }
    eventPayload.formData[key] = {
      filename: file.name,
      size: file.size,
      type: file.type || "application/octet-stream",
    };
    eventPayload.formLabels[key] = field.label;
    body.append(key, file, file.name);
  }

  for (const key of Object.keys(eventPayload.formData)) {
    eventPayload.formLabels[key] ||= key;
  }

  body.append("event", JSON.stringify(eventPayload));

  fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
    method: "POST",
    headers: {
      version: "2021-07-28",
    },
    body,
  }).catch(() => {}); // Fire-and-forget — don't block form UX
};

// Reuse the published site's analytics session (set by /__vibe/analytics.js) so the
// submission joins the visit's page views; fall back to a fresh id when it is absent
// or unreadable (document.cookie throws in a sandboxed iframe).
const getTrackingSessionId = () => {
  try {
    const sid = document.cookie.match(
      /(?:^|;\s*)__vibe_sid=([0-9a-f-]{36})(?:;|$)/,
    )?.[1];
    if (sid) return sid;
  } catch {}
  return crypto.randomUUID();
};

export interface ConsultationFormData {
  fullName: string;
  email: string;
  phone: string;
  address: string;
  propertyType: string;
  unitsCount: string;
  message: string;
}

/**
 * Submits consultation form data to the CRM.
 * Standard fields map to standard CRM contact keys; non-standard fields
 * use registered custom-field IDs.
 */
export function submitConsultationToCRM(
  data: ConsultationFormData,
  formId: string,
  formName: string,
): void {
  // Split full name into first / last for standard CRM fields.
  const nameParts = data.fullName.trim().split(/\s+/);
  const first_name = nameParts[0] || "";
  const last_name = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

  const trackingPayload = {
    type: "external_form_submission",
    timestamp: Date.now(),
    formId,
    formData: {
      first_name,
      last_name,
      email: data.email,
      phone: data.phone,
      address: data.address,
      calendar_notes: data.message,
    },
    formLabels: {
      first_name: "Full Name",
      last_name: "Full Name",
      email: "Email Address",
      phone: "Phone Number",
      address: "Property Address / City",
      calendar_notes: "How can we assist you?",
    },
    url: window.location.href,
    title: document.title,
    path: window.location.pathname,
    userAgent: navigator.userAgent,
    trackingId: "tk_f59d315f940d496f82610a928cb2abc2",
    locationId: "mT80PbegrbDSOV5X6nOi",
    projectId: "1785535147473014979",
    sessionId: getTrackingSessionId(),
    properties: {
      deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
        ? "mobile"
        : "desktop",
      source: "ai_studio",
      projectId: "1785535147473014979",
      formName,
    },
  };

  postTrackingEvent(trackingPayload, {
    customFields: {
      Awda9jcpWiJI4jk8LMjM: {
        value: data.propertyType,
        label: "Property Category",
      },
      FXWTgInlPnLCFDdLWORl: {
        value: data.unitsCount,
        label: "Units Count",
      },
    },
  });
}
